import { UzErrorState, UzSkeleton } from '@utazzo/ui';
import { motion } from 'framer-motion';
import React, { useEffect, useRef } from 'react';

import { WelcomeContent } from '../components/WelcomeContent';
import { useWelcomePage } from '../welcome.api';

import { useWelcomeGate } from '@/app/welcome-gate';
import { fadeUp } from '@/lib/motion';

/**
 * Welcome Utazzer - the CRM landing page every staff member sees after
 * sign-in. Read-only for everyone; ADMIN edits it from the sidebar
 * (Website -> Welcome Page, /welcome-page).
 */
export const WelcomePage = (): React.JSX.Element => {
  const { data, isLoading, isError, refetch } = useWelcomePage();

  // Release the welcome gate once the user has scrolled to the end of the page.
  // The bottom sentinel coming into view = they've seen the whole thing (if the
  // content is shorter than the viewport it is already visible, which is fine -
  // there was nothing to scroll). See welcome-gate + AppShell.
  const acknowledgeWelcome = useWelcomeGate((s) => s.acknowledge);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = endRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          acknowledgeWelcome();
          obs.disconnect();
        }
      },
      { root: null, threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [acknowledgeWelcome, data]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <UzSkeleton className="aspect-[1250/600] w-full rounded-[13.55px]" />
        <UzSkeleton className="h-8 w-64" />
        <UzSkeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-5xl">
        <UzErrorState
          title="Couldn’t load the welcome page"
          description="Check your connection and try again."
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="show" variants={fadeUp} className="mx-auto max-w-5xl">
      <WelcomeContent content={data.content} websiteStars={data.websiteStars} media={data.media} />
      {/* Scroll sentinel: when this reaches the viewport the welcome gate releases. */}
      <div ref={endRef} aria-hidden className="h-px w-full" />
    </motion.div>
  );
};
